import { useState, useEffect } from 'react';
import { X, RefreshCw, MapPin, AlertTriangle, ChevronRight, Calendar, Truck } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { C2Event, EventStatus } from '../../types';

type EventTab = 'open' | 'supervision' | 'closed';

const eventTabs: { id: EventTab; label: string }[] = [
  { id: 'open', label: 'Open Events' },
  { id: 'supervision', label: 'Supervision' },
  { id: 'closed', label: 'Closed Events' },
];

const tabStatusMap: Record<EventTab, EventStatus[]> = {
  open: ['open'],
  supervision: ['supervision'],
  closed: ['closed'],
};

const categoryIcon: Record<string, string> = {
  festival: '🎵',
  sporting: '⚽',
  protest: '📢',
  emergency: '🚨',
  training: '🎯',
  planned_operation: '📋',
  natural_disaster: '🌊',
  public_order: '🛡️',
  other: '📌',
};

const statusColors: Record<string, string> = {
  open: 'text-emerald-400',
  supervision: 'text-amber-400',
  closed: 'text-gray-400',
};

interface ViewEventsModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (event: C2Event) => void;
}

export default function ViewEventsModal({ open, onClose, onSelect }: ViewEventsModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState<EventTab>('open');
  const [events, setEvents] = useState<C2Event[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEvents = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchEvents();
  }, [open, token]);

  const filtered = events.filter((evt) =>
    tabStatusMap[activeTab].includes(evt.status)
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-[#0f1526] border border-white/[0.08] rounded-2xl shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Events</h2>
            <p className="text-xs text-white/30 mt-0.5">Select an event to open its workspace.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors">
            <X className="w-4 h-4 text-white/40" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-white/[0.06] flex-shrink-0">
          {eventTabs.map((tab) => {
            const count = events.filter((e) => tabStatusMap[tab.id].includes(e.status)).length;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-2 text-xs font-semibold transition-all relative flex items-center gap-1.5',
                  activeTab === tab.id
                    ? 'text-primary'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                {tab.label}
                {count > 0 && (
                  <span className={cn(
                    'min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1',
                    activeTab === tab.id ? 'bg-primary/20 text-primary' : 'bg-white/[0.06] text-white/30'
                  )}>
                    {count}
                  </span>
                )}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-full" />
                )}
              </button>
            );
          })}
          <div className="ml-auto">
            <button
              onClick={fetchEvents}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold text-white/40 bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
            >
              <RefreshCw className={cn('w-3 h-3', loading && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </div>

        {/* Event List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading && filtered.length === 0 ? (
            <div className="text-center py-12 text-white/20 text-xs">
              <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin text-white/10" />
              Loading events...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-white/10" />
              <p className="text-xs text-white/30">No events in this category</p>
              <p className="text-[10px] text-white/15 mt-1">
                {activeTab === 'open' && 'Create a new event to get started.'}
                {activeTab === 'supervision' && 'Events set to supervision mode will appear here.'}
                {activeTab === 'closed' && 'Closed events will appear here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((evt) => (
                <button
                  key={evt.id}
                  onClick={() => onSelect(evt)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all text-left group active:scale-[0.99]"
                >
                  {/* Category icon */}
                  <div className="w-11 h-11 rounded-xl bg-white/[0.04] flex items-center justify-center text-lg flex-shrink-0">
                    {categoryIcon[evt.category] || '📌'}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono font-bold text-white/40">{evt.id}</span>
                      <span className={cn(
                        'text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border border-white/[0.08] bg-white/[0.04]',
                        evt.category === 'emergency' ? 'text-red-400' : 'text-white/50'
                      )}>
                        {evt.category.replace(/_/g, ' ')}
                      </span>
                      <span className={cn(
                        'text-[9px] font-semibold capitalize',
                        statusColors[evt.status] || 'text-white/30'
                      )}>
                        {evt.status}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-white/90 truncate">{evt.name}</h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      {evt.location?.address && (
                        <span className="flex items-center gap-1 text-[10px] text-white/30">
                          <MapPin className="w-2.5 h-2.5" />
                          {evt.location.address}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[10px] text-white/30">
                        <Calendar className="w-2.5 h-2.5" />
                        {evt.eventDate} {evt.eventStartTime}
                      </span>
                      {evt.assignedResources.length > 0 && (
                        <span className="flex items-center gap-1 text-[10px] text-white/30">
                          <Truck className="w-2.5 h-2.5" />
                          {evt.assignedResources.length} resource{evt.assignedResources.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-4 h-4 text-white/10 group-hover:text-white/30 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
